import { Directive, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, inject } from '@angular/core';

/**
 * Makes a fixed-position document preview overlay behave correctly wherever its page is rendered.
 *
 * Put it on the backdrop element, inside the `@if` that shows the preview — the element is created
 * when the preview opens and destroyed when it closes, so this directive's lifecycle *is* the
 * preview's lifecycle. It handles the two things CSS alone cannot:
 *
 * 1. Freezes the page behind the overlay so it cannot scroll away from what the user was looking
 *    at when they hit Preview. Only `overflow` is touched, so the scroller keeps its height and
 *    its scrollTop, and there is nothing to restore on close.
 * 2. Lifts any enclosing Material tab body above the tab strip. These pages render inside tabbed
 *    navigation shells that style `.mat-mdc-tab-header` as `position: sticky; z-index: 5`, while
 *    Material makes the active `.mat-mdc-tab-body` a stacking context
 *    (`position: relative; z-index: 1`). That seals the overlay in at level 1, so the tabs paint
 *    over the panel and no z-index on the overlay itself can escape.
 *
 * Emits `dismiss` on Escape and on a click landing on the backdrop itself (clicks bubbling out of
 * the panel are ignored, so the template does not need its own `stopPropagation`).
 */
@Directive({
  selector: '[appDocumentPreviewOverlay]'
})
export class DocumentPreviewOverlayDirective implements OnInit, OnDestroy {
  /** User asked to close the preview — bind this to the host's close handler. */
  @Output() dismiss = new EventEmitter<void>();

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  /** Must clear the `z-index: 5` the navigation shells give `.mat-mdc-tab-header`. */
  private readonly TAB_BODY_STACK_LEVEL = 1000;

  private scrollHost: HTMLElement | null = null;
  private scrollHostOverflow = '';
  private liftedTabBodies: { el: HTMLElement, zIndex: string }[] = [];

  ngOnInit() {
    this.lockBackgroundScroll();
    this.liftTabBodiesAboveTabHeader();
  }

  ngOnDestroy() {
    this.unlockBackgroundScroll();
    this.restoreTabBodies();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    this.dismiss.emit();
  }

  @HostListener('click', ['$event'])
  onBackdropClick(event: MouseEvent) {
    if (event.target === this.elementRef.nativeElement) {
      this.dismiss.emit();
    }
  }

  private lockBackgroundScroll() {
    const host = this.findScrollHost();
    if (!host) {
      return;
    }
    this.scrollHost = host;
    this.scrollHostOverflow = host.style.overflow;
    host.style.overflow = 'hidden';
  }

  private unlockBackgroundScroll() {
    if (!this.scrollHost) {
      return;
    }
    this.scrollHost.style.overflow = this.scrollHostOverflow;
    this.scrollHost = null;
    this.scrollHostOverflow = '';
  }

  private liftTabBodiesAboveTabHeader() {
    let el = this.elementRef.nativeElement.closest<HTMLElement>('.mat-mdc-tab-body');
    while (el) {
      this.liftedTabBodies.push({ el, zIndex: el.style.zIndex });
      el.style.zIndex = String(this.TAB_BODY_STACK_LEVEL);
      el = el.parentElement?.closest<HTMLElement>('.mat-mdc-tab-body') ?? null;
    }
  }

  private restoreTabBodies() {
    for (const { el, zIndex } of this.liftedTabBodies) {
      el.style.zIndex = zIndex;
    }
    this.liftedTabBodies = [];
  }

  /**
   * Nearest scrollable ancestor. This differs per host — `mat-sidenav-content` on a standalone
   * route, `.mat-mdc-tab-body-wrapper` inside the tabbed shells, the document on mobile — so it
   * is discovered by walking up rather than hardcoded to one selector.
   */
  private findScrollHost(): HTMLElement | null {
    let el = this.elementRef.nativeElement.parentElement;
    while (el) {
      const overflowY = getComputedStyle(el).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
        return el;
      }
      el = el.parentElement;
    }
    return document.scrollingElement as HTMLElement | null;
  }
}
